import React from "react";
import { List, useTable, DateField, TextField, SaveButton, TagField } from "@refinedev/antd";
import { Form, FormProps, Input, Row, Col, Card, Divider, Tag, Select, Space } from "antd";
import { Table, Typography, Button } from "antd";
import type { ColumnsType } from "antd/es/table";
import { IAuditLog } from "../../interfaces";
import { CrudFilters, getDefaultFilter, HttpError, useTable as useTableCore } from "@refinedev/core";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { getStatusTagColor } from "constants/tagColors";

export enum AuditLogStatus {
  STARTED = 'STARTED',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

const Filter: React.FC<{ 
  formProps: FormProps; 
  filters: CrudFilters;
  setFilters: (filters: CrudFilters, behavior?: "merge" | "replace") => void;
}> = (props) => {
  const { formProps, filters, setFilters } = props;

  return (
    <Form
      layout="vertical"
      {...formProps}
      initialValues={{
        status: getDefaultFilter("status", filters),
        correlationId: getDefaultFilter("correlationId", filters),
        imoNumber: getDefaultFilter("imoNumber", filters),
      }}
    >
      <Row gutter={[16, 16]} align="middle" justify="space-between">
        <Col xs={24} sm={24} md={8}>
          <Form.Item label="Status" name="status">
            <Select
              placeholder="Select status"
              allowClear
              options={[
                { label: "STARTED", value: AuditLogStatus.STARTED },
                { label: "COMPLETED", value: AuditLogStatus.COMPLETED },
                { label: "FAILED", value: AuditLogStatus.FAILED },
              ]}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Form.Item label="IMO Number" name="imoNumber">
            <Input placeholder="Filter by IMO number" allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Form.Item label="Correlation ID" name="correlationId">
            <Input placeholder="Filter by correlation ID" allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={24}>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button 
                onClick={() => {
                  formProps.form?.resetFields();
                  setFilters([], "replace");
                  formProps.form?.setFieldsValue({ status: undefined });
                }} 
                size="large"
                icon={<ReloadOutlined />}
              >
                Reset
              </Button>
              <Button 
                onClick={formProps.form?.submit} 
                size="large"
                type="primary"
                icon={<SearchOutlined />}
              >
                Filter
              </Button>
            </Space>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export const AuditLogList: React.FC = () => {
  const { tableProps, sorters, tableQuery: { isLoading }, searchFormProps, filters, setFilters } =
    useTable<IAuditLog, HttpError>({
      resource: "audit-logs",
      syncWithLocation: false,

      onSearch: (params: any) => {
        const filters: CrudFilters = [];
        const { status, correlationId, imoNumber } = params;

        if (status) {
          filters.push({
            field: "status",
            operator: "eq",
            value: status,
          });
        }

        if (correlationId) {
          filters.push({
            field: "correlationId",
            operator: "eq",
            value: correlationId,
          });
        }

        if (imoNumber) {
          filters.push({
            field: "imoNumber",
            operator: "eq",
            value: imoNumber,
          });
        } else {
          filters.push({
            field: "imoNumber",
            operator: "eq",
            value: null,
          });
        }

        return filters;
      },

      sorters: {
        initial: [
          {
            field: "imoNumber",
            order: "asc",
          },
          {
            field: "createdAt",
            order: "asc",
          },
        ]
      }
    });

  const columns: ColumnsType<IAuditLog> = [
    {
      title: "Process",
      key: "process",
      width: 300,
      render: (_, record) => (
        <Space direction="vertical" size={0} style={{ whiteSpace: 'nowrap' }}>
          <Space>
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              ID:
            </Typography.Text>
            <Typography.Text copyable>{record.correlationId}</Typography.Text>
          </Space>
          <Space direction="vertical" size={0} style={{ marginTop: 4 }}>
            <Typography.Text strong>{record.processName}</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              {record.stepName}
            </Typography.Text>
          </Space>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (value) => (
        <TagField value={value} color={getStatusTagColor(value)} />
      ),
      sorter: true,
    },
    {
      title: "Log Entry",
      dataIndex: "logEntry",
      key: "logEntry",
      width: 300,
      className: "wrap-text",
      render: (value) => (
        <Typography.Text style={{ maxWidth: 300, whiteSpace: 'normal' }}>
          {value}
        </Typography.Text>
      ),
    },
    {
      title: "Vessel Name",
      dataIndex: "vesselName",
      key: "vesselName",
      width: 180,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
    },
    {
      title: "IMO Number",
      dataIndex: "imoNumber",
      key: "imoNumber",
      width: 130,
      render: (value) => <TextField value={value} />,
    },
    {
      title: "Date",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 200,
      render: (value) => <DateField value={value} format="LLL" />,
      sorter: true,
      defaultSortOrder: "descend",
    },
  ];

  return (
    <List
      breadcrumb={false}
      headerProps={{
        title: "Audit Logs",
        subTitle: "system activity logs",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        ghost: true,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Row gutter={[16, 16]}>
        <Col xl={24} lg={24} xs={24}>
          <Card type="inner" title="Filter">
            <Filter formProps={searchFormProps} filters={filters || []} setFilters={setFilters} />
          </Card>
        </Col>
        <Col xl={24} xs={24}>
          <Table
            {...tableProps}
            columns={columns}
            rowKey="id"
            size="middle"
            scroll={{ x: 1200 }}
            sticky
            pagination={{
              ...tableProps.pagination,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
            }}
          />
        </Col>
      </Row>
    </List>
  );
}; 